import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, getApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateLong } from "@/lib/format";
import { orgToday } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Landmark, Crown, UserPlus, Users, CalendarDays, Plus, Pencil, Trash2, Loader2, Paperclip, Download, X, Upload,
} from "lucide-react";

const ACCENT = "#6b4f8a";
const MAX_MB = 25;

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function SectionTitle({ icon: Icon, children, action }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: ACCENT }} />
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{children}</h2>
      </div>
      {action}
    </div>
  );
}

// The Management Team and the Executive Team share this page; only the API and wording differ.
const TEAMS = {
  management: {
    title: "Management Team", icon: Landmark, meetingTitle: "Management meeting", allowed: (can) => can.management,
    intro: "Team members can see draft and archived documents, and every member must approve a policy before it goes Active. "
      + "Meeting notes here are visible to the Management Team, Executive Team and Admins. Joint meetings with the Executive Team also show on the Executive Team page.",
    otherTeam: "Executive Team",
    emptyHint: " Add the members who approve policies.",
    removeWarning: "They will lose management access straight away (unless they're also on the Executive Team). "
      + "Approvals on every draft awaiting approval will be cleared, so the team (including anyone who replaces them) approves it again.",
    api: {
      team: api.govTeam, candidates: api.govTeamCandidates, add: api.govAddMember, update: api.govUpdateMember,
      remove: api.govRemoveMember, meetings: api.govMeetings, createMeeting: api.govCreateMeeting,
      updateMeeting: api.govUpdateMeeting, deleteMeeting: api.govDeleteMeeting, addAttachment: api.govAddAttachment,
      attachmentFile: api.govAttachmentFile, deleteAttachment: api.govDeleteAttachment,
    },
  },
  executive: {
    title: "Executive Team", icon: Crown, meetingTitle: "Executive meeting", allowed: (can) => can.executive,
    intro: "Team members can see, edit and manage documents like the Management Team, but don't approve them. "
      + "They also receive the weekly NDIS Commission update. Executive meeting notes are visible to the Executive Team and Admins only; "
      + "joint meetings with the Management Team are visible to both teams.",
    otherTeam: "Management Team",
    emptyHint: " Add your executives and board members.",
    removeWarning: "They will lose access to Executive meetings straight away.",
    api: {
      team: api.govExecTeam, candidates: api.govExecTeamCandidates, add: api.govExecAddMember, update: api.govExecUpdateMember,
      remove: api.govExecRemoveMember, meetings: api.govExecMeetings, createMeeting: api.govExecCreateMeeting,
      updateMeeting: api.govExecUpdateMeeting, deleteMeeting: api.govExecDeleteMeeting, addAttachment: api.govExecAddAttachment,
      attachmentFile: api.govExecAttachmentFile, deleteAttachment: api.govExecDeleteAttachment,
    },
  },
};

export default function ManagementTeam({ team = "management" }) {
  const { can, user } = useAuth();
  const isAdmin = can.has("docs.admin");
  const T = TEAMS[team];
  const TeamIcon = T.icon;
  const [members, setMembers] = useState(null);
  const [people, setPeople] = useState({ management: [], executive: [] });   // both teams, for joint meetings
  const [meetings, setMeetings] = useState(null);
  const [memberDlg, setMemberDlg] = useState(null);   // { username?, existing?, position, candidates }
  const [meetingDlg, setMeetingDlg] = useState(null); // { id?, date, title, attendees:Set, summary }
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRef = useRef(null);
  const uploadTarget = useRef(null);

  const loadMembers = useCallback(() => T.api.team().then((r) => setMembers(r.members))
    .catch((e) => { setMembers([]); toast.error(getApiError(e, "Couldn't load the team.")); }), [T]);
  const loadMeetings = useCallback(() => T.api.meetings().then((r) => setMeetings(r.meetings))
    .catch((e) => { setMeetings([]); toast.error(getApiError(e, "Couldn't load meetings.")); }), [T]);
  const loadPeople = useCallback(() => api.govTeamsPeople().then(setPeople).catch(() => {}), []);
  useEffect(() => { loadMembers(); loadMeetings(); loadPeople(); }, [loadMembers, loadMeetings, loadPeople]);

  const nameOf = useMemo(() => {
    const m = {};
    [...people.management, ...people.executive, ...(members || [])].forEach((x) => { m[x.username] = x.full_name; });
    return (u) => m[u] || u;
  }, [members, people]);

  // Joint meetings are stored with the Management Team's meetings, whichever page they're opened from.
  const apiFor = (m) => (m?.joint ? TEAMS.management.api : T.api);
  const jointPeople = useMemo(() => {
    const byId = {};
    people.management.forEach((p) => { byId[p.username] = { ...p, teams: ["Management"] }; });
    people.executive.forEach((p) => { byId[p.username] = byId[p.username] ? { ...byId[p.username], teams: ["Management", "Executive"] } : { ...p, teams: ["Executive"] }; });
    return Object.values(byId).sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [people]);

  // ---- members ----
  const openAdd = async () => {
    try {
      const r = await T.api.candidates();
      setMemberDlg({ username: "", position: "", candidates: r.candidates });
    } catch (e) { toast.error(getApiError(e, "Couldn't load users.")); }
  };

  const saveMember = async () => {
    setSaving(true);
    try {
      if (memberDlg.existing) await T.api.update(memberDlg.existing.username, memberDlg.position);
      else await T.api.add({ username: memberDlg.username, position: memberDlg.position });
      toast.success(memberDlg.existing ? "Member updated" : `Added to the ${T.title}`);
      setMemberDlg(null);
      loadMembers(); loadPeople();
    } catch (e) { toast.error(getApiError(e, "Couldn't save.")); }
    finally { setSaving(false); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.full_name} from the ${T.title}? ${T.removeWarning}`)) return;
    try {
      const r = await T.api.remove(m.username);
      toast.success(r.approvals_reset
        ? `Removed from the team. Approvals were cleared on ${r.approvals_reset} draft${r.approvals_reset === 1 ? "" : "s"} awaiting approval.`
        : "Removed from the team");
      loadMembers(); loadPeople();
    }
    catch (e) { toast.error(getApiError(e, "Couldn't remove.")); }
  };

  // ---- meetings ----
  const openMeeting = (m) => setMeetingDlg(m
    ? { id: m.id, date: m.date, title: m.title, attendees: new Set(m.attendees), summary: m.summary, joint: m.joint }
    : { date: orgToday(), title: T.meetingTitle, attendees: new Set((members || []).map((x) => x.username)), summary: "", joint: false });

  // On the Executive page an existing meeting can't switch between joint and executive-only
  // (they're kept in different places); on the Management page it's just a flag.
  const jointLocked = !!meetingDlg?.id && team !== "management";
  const setJoint = (on) => setMeetingDlg((d) => {
    const attendees = new Set(d.attendees);
    if (on && !d.id) people[team === "management" ? "executive" : "management"].forEach((p) => attendees.add(p.username));
    return { ...d, joint: on, title: !d.id && d.title === T.meetingTitle && on ? "Joint Management & Executive meeting" : d.title, attendees };
  });

  const saveMeeting = async () => {
    const payload = { date: meetingDlg.date, title: meetingDlg.title, attendees: [...meetingDlg.attendees], summary: meetingDlg.summary,
      joint: !!meetingDlg.joint };
    const a = apiFor(meetingDlg);
    setSaving(true);
    try {
      if (meetingDlg.id) await a.updateMeeting(meetingDlg.id, payload);
      else await a.createMeeting(payload);
      toast.success(meetingDlg.id ? "Meeting updated" : "Meeting added");
      setMeetingDlg(null);
      loadMeetings();
    } catch (e) { toast.error(getApiError(e, "Couldn't save the meeting.")); }
    finally { setSaving(false); }
  };

  const deleteMeeting = async (m) => {
    if (!window.confirm(`Delete "${m.title}" (${fmtDateLong(m.date)}) and its attached notes?`)) return;
    try { await apiFor(m).deleteMeeting(m.id); toast.success("Meeting deleted"); loadMeetings(); }
    catch (e) { toast.error(getApiError(e, "Couldn't delete.")); }
  };

  const pickFile = (meeting) => { uploadTarget.current = meeting; fileRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const target = uploadTarget.current;
    const mid = target?.id;
    if (!file || !mid) return;
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`File must be under ${MAX_MB} MB.`); return; }
    setUploadingId(mid);
    try {
      await apiFor(target).addAttachment(mid, file.name, await fileToDataUrl(file));
      toast.success("Notes uploaded");
      loadMeetings();
    } catch (err) { toast.error(getApiError(err, "Upload failed.")); }
    finally { setUploadingId(null); }
  };

  const download = async (m, a) => {
    try {
      const blob = await apiFor(m).attachmentFile(m.id, a.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a"); link.href = url; link.download = a.filename; link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); }
  };

  const removeAttachment = async (m, a) => {
    if (!window.confirm(`Remove "${a.filename}" from this meeting?`)) return;
    try { await apiFor(m).deleteAttachment(m.id, a.id); loadMeetings(); }
    catch (e) { toast.error(getApiError(e, "Couldn't remove the file.")); }
  };

  if (!T.allowed(can)) return null;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid={`${team}-team`}>
      <div className="flex items-center gap-2.5">
        <TeamIcon className="h-5 w-5" style={{ color: ACCENT }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">{T.title}</h1>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-5">{T.intro}</p>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <SectionTitle icon={Users} action={isAdmin && (
          <Button size="sm" className="h-8 text-xs" style={{ backgroundColor: ACCENT }} onClick={openAdd} data-testid="team-add">
            <UserPlus className="h-3.5 w-3.5 mr-1" /> Add member
          </Button>
        )}>Current members</SectionTitle>
        {members === null ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : !members.length ? (
          <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">
            No one is on the {T.title} yet.{isAdmin ? T.emptyHint : " An Admin can add members."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="team-list">
            {members.map((m) => (
              <div key={m.username} className="flex items-center gap-3 py-2.5 group">
                <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0" style={{ backgroundColor: ACCENT }}>
                  {m.full_name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{m.full_name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{[m.position, m.username].filter(Boolean).join(" · ")}</div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button className="text-slate-400 hover:text-[#6b4f8a] p-1.5 rounded-md hover:bg-slate-100" title="Edit position"
                      onClick={() => setMemberDlg({ existing: m, position: m.position })} data-testid={`team-edit-${m.username}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button className="text-slate-400 hover:text-rose-600 p-1.5 rounded-md hover:bg-rose-50" title="Remove from team"
                      onClick={() => removeMember(m)} data-testid={`team-remove-${m.username}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {!isAdmin && members?.length > 0 && <div className="text-[11px] text-slate-400 mt-2">Only an Admin can change who is on the team.</div>}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mt-5">
        <SectionTitle icon={CalendarDays} action={
          <Button size="sm" className="h-8 text-xs" style={{ backgroundColor: ACCENT }} onClick={() => openMeeting(null)} data-testid="meeting-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add meeting
          </Button>
        }>Meetings</SectionTitle>
        {meetings === null ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : !meetings.length ? (
          <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">No meetings recorded yet.</div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="meeting-list">
            {meetings.map((m) => (
              <div key={m.id} className="py-3 group" data-testid={`meeting-${m.id}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-2">{m.title}
                      {m.joint && <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full border border-[#6b4f8a40] text-[#6b4f8a]" data-testid="meeting-joint-badge">Joint</span>}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {fmtDateLong(m.date)}
                      {m.attendees.length > 0 && <> · Attendees: {m.attendees.map(nameOf).join(", ")}</>}
                    </div>
                    {m.summary && <div className="text-xs text-slate-600 mt-1 whitespace-pre-line">{m.summary}</div>}
                  </div>
                  <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                    <button className="text-slate-400 hover:text-[#6b4f8a] p-1.5 rounded-md hover:bg-slate-100" title="Attach notes"
                      onClick={() => pickFile(m)} disabled={uploadingId === m.id} data-testid={`meeting-upload-${m.id}`}>
                      {uploadingId === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    </button>
                    <button className="text-slate-400 hover:text-[#6b4f8a] p-1.5 rounded-md hover:bg-slate-100" title="Edit"
                      onClick={() => openMeeting(m)} data-testid={`meeting-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button className="text-slate-400 hover:text-rose-600 p-1.5 rounded-md hover:bg-rose-50" title="Delete"
                      onClick={() => deleteMeeting(m)} data-testid={`meeting-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                {m.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {m.attachments.map((a) => (
                      <span key={a.id} className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-full pl-2 pr-1 py-0.5">
                        <Paperclip className="h-3 w-3 text-slate-400" />
                        <button className="hover:text-[#6b4f8a] hover:underline" onClick={() => download(m, a)} title="Download">{a.filename}</button>
                        <span className="text-slate-400">({fmtSize(a.size)})</span>
                        <Download className="h-3 w-3 text-slate-300" />
                        <button className="p-0.5 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50" onClick={() => removeAttachment(m, a)} title="Remove"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <input ref={fileRef} type="file" className="hidden" onChange={onFile}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg" data-testid="meeting-file-input" />
      </div>

      {/* Add / edit member */}
      <Dialog open={!!memberDlg} onOpenChange={(o) => !o && setMemberDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-brand text-base">{memberDlg?.existing ? `Edit ${memberDlg.existing.full_name}` : "Add a team member"}</DialogTitle>
          </DialogHeader>
          {memberDlg && (
            <div className="space-y-3 pt-1">
              {!memberDlg.existing && (
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Person</Label>
                  <Select value={memberDlg.username} onValueChange={(v) => setMemberDlg((d) => ({ ...d, username: v }))}>
                    <SelectTrigger className="h-9 text-sm bg-white" data-testid="team-person"><SelectValue placeholder="Choose an office user…" /></SelectTrigger>
                    <SelectContent>
                      {memberDlg.candidates.map((c) => <SelectItem key={c.username} value={c.username} className="text-sm">{c.full_name} · {c.username}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Position (optional)</Label>
                <Input value={memberDlg.position} placeholder="e.g. Director, Operations Manager" className="h-9 text-sm" data-testid="team-position"
                  onChange={(e) => setMemberDlg((d) => ({ ...d, position: e.target.value }))} />
              </div>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" size="sm" onClick={() => setMemberDlg(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: ACCENT }} onClick={saveMember} data-testid="team-save"
              disabled={saving || (!memberDlg?.existing && !memberDlg?.username)}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{memberDlg?.existing ? "Save" : "Add to team"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / edit meeting */}
      <Dialog open={!!meetingDlg} onOpenChange={(o) => !o && setMeetingDlg(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-brand text-base">{meetingDlg?.id ? "Edit meeting" : "Add meeting"}</DialogTitle>
          </DialogHeader>
          {meetingDlg && (
            <div className="space-y-3 pt-1">
              <div className="grid grid-cols-[150px_1fr] gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Date</Label>
                  <Input type="date" value={meetingDlg.date} className="h-9 text-sm" data-testid="meeting-date"
                    onChange={(e) => setMeetingDlg((d) => ({ ...d, date: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Title</Label>
                  <Input value={meetingDlg.title} className="h-9 text-sm" data-testid="meeting-title"
                    onChange={(e) => setMeetingDlg((d) => ({ ...d, title: e.target.value }))} />
                </div>
              </div>
              <label className={`flex items-start gap-2 text-sm text-slate-700 ${jointLocked ? "opacity-60" : "cursor-pointer"}`}>
                <Checkbox checked={!!meetingDlg.joint} disabled={jointLocked} onCheckedChange={(v) => setJoint(!!v)} data-testid="meeting-joint" />
                <span>Joint meeting with the {T.otherTeam}
                  <span className="block text-[11px] text-slate-500">
                    {jointLocked ? "This can't be changed for an existing meeting here."
                      : "Shows on both teams' pages and is visible to both teams."}
                  </span>
                </span>
              </label>
              <div className="space-y-1">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Attendees</Label>
                {!(meetingDlg.joint ? jointPeople : members || []).length ? (
                  <div className="text-xs text-slate-400">Add team members first to record attendance.</div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {(meetingDlg.joint ? jointPeople : members).map((m) => (
                      <label key={m.username} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <Checkbox checked={meetingDlg.attendees.has(m.username)}
                          onCheckedChange={(v) => setMeetingDlg((d) => {
                            const s = new Set(d.attendees); if (v) s.add(m.username); else s.delete(m.username);
                            return { ...d, attendees: s };
                          })} />
                        {m.full_name}
                        {meetingDlg.joint && <span className="text-[10px] text-slate-400">{m.teams.join(" & ")}</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Summary (optional)</Label>
                <Textarea value={meetingDlg.summary} rows={3} className="text-sm" placeholder="Key decisions or a short note. Attach the full minutes after saving."
                  onChange={(e) => setMeetingDlg((d) => ({ ...d, summary: e.target.value }))} data-testid="meeting-summary" />
              </div>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" size="sm" onClick={() => setMeetingDlg(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: ACCENT }} onClick={saveMeeting} data-testid="meeting-save"
              disabled={saving || !meetingDlg?.date || !meetingDlg?.title?.trim()}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{meetingDlg?.id ? "Save" : "Add meeting"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
